/**
 * Dev/testing transport: wraps `INetworkServer` / `INetworkConnection` so each
 * accepted socket observes optional delay and packet loss. Production code
 * should use `WebSocketNetworkServer` directly; opt in only via
 * {@link createLagSimulatingServerFactory}.
 */
import { Delegate } from '../../utils/Delegate.js';

import type { ConnectionStatsSnapshot, INetworkConnection, INetworkServer } from '../interface.js';
import type { NetworkConnectionFactory } from './NetworkGameSession.js';
import type { NetworkServerFactory } from './ServerGameSession.js';

/**
 * Lag simulation knobs. Mutate the same object to reconfigure live connections.
 */
export interface LagSimulationConfig {
  /** Whether to enable lag simulation */
  enabled: boolean;
  /** Minimum one-way delay in ms (used with `maxDelay`) */
  minDelay: number;
  /** Maximum one-way delay in ms */
  maxDelay: number;
  /** Drop probability per message, 0–100 */
  packetLoss: number;
}

export const DEFAULT_LAG_SIMULATION_CONFIG: LagSimulationConfig = {
  enabled: false,
  minDelay: 0,
  maxDelay: 0,
  packetLoss: 0
};

function shouldDrop(config: LagSimulationConfig): boolean {
  if (!config.enabled || config.packetLoss <= 0) {
    return false;
  }
  return Math.random() * 100 < config.packetLoss;
}

async function delayIfNeeded(config: LagSimulationConfig): Promise<void> {
  if (!config.enabled) {
    return;
  }
  const lo = Math.min(config.minDelay, config.maxDelay);
  const hi = Math.max(config.minDelay, config.maxDelay);
  if (hi <= 0) {
    return;
  }
  const ms = Math.random() * (hi - lo) + lo;
  if (ms > 0) {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Wraps an underlying connection: inbound and outbound payloads may be
 * delayed or dropped per {@link LagSimulationConfig}.
 */
export class LagSimulatingNetworkConnection implements INetworkConnection {
  public readonly onOpen: Delegate<[], void> = new Delegate();
  public readonly onMessage: Delegate<[Uint8Array], void> = new Delegate();
  public readonly onClose: Delegate<[number, string], void> = new Delegate();
  public readonly onError: Delegate<[Error], void> = new Delegate();

  private closed = false;
  private droppedInbound = 0;
  private droppedOutbound = 0;

  constructor(
    private readonly inner: INetworkConnection,
    private readonly config: LagSimulationConfig
  ) {
    this.inner.onOpen.add(() => {
      this.onOpen.invoke();
    });
    this.inner.onMessage.add((data) => {
      void this.handleInboundMessage(data);
    });
    this.inner.onClose.add((code, reason) => {
      this.closed = true;
      this.onClose.invoke(code, reason);
    });
    this.inner.onError.add((error) => {
      this.onError.invoke(error);
    });
  }

  private async handleInboundMessage(data: Uint8Array): Promise<void> {
    const copy = new Uint8Array(data);
    if (!this.config.enabled) {
      this.onMessage.invoke(copy);
      return;
    }
    if (shouldDrop(this.config)) {
      this.droppedInbound++;
      return;
    }
    await delayIfNeeded(this.config);
    if (this.closed || !this.inner.isOpen()) {
      return;
    }
    this.onMessage.invoke(copy);
  }

  private async sendAsync(data: Uint8Array): Promise<void> {
    const copy = new Uint8Array(data);
    if (!this.config.enabled) {
      this.inner.send(copy);
      return;
    }
    if (shouldDrop(this.config)) {
      this.droppedOutbound++;
      return;
    }
    await delayIfNeeded(this.config);
    if (this.closed || !this.inner.isOpen()) {
      return;
    }
    this.inner.send(copy);
  }

  public isOpen(): boolean {
    return this.inner.isOpen();
  }

  public connect(): void {
    this.inner.connect();
  }

  public send(data: Uint8Array): void {
    void this.sendAsync(data);
  }

  public close(code?: number, reason?: string): void {
    this.closed = true;
    this.inner.close(code, reason);
  }

  public getStats(): ConnectionStatsSnapshot {
    const base = this.inner.getStats();
    const lagExtra: Record<string, number> = {
      lagSimDroppedInbound: this.droppedInbound,
      lagSimDroppedOutbound: this.droppedOutbound
    };
    return {
      ...base,
      extra: base.extra ? { ...base.extra, ...lagExtra } : lagExtra
    };
  }

  public recordRttSample(rttMs: number): void {
    this.inner.recordRttSample(rttMs);
  }
}

/**
 * Returns a {@link NetworkConnectionFactory} that wraps the inner transport with
 * lag simulation. Share the same `config` object to update reconnects.
 */
export function createLagSimulatingConnectionFactory(
  innerFactory: NetworkConnectionFactory,
  config: LagSimulationConfig
): NetworkConnectionFactory {
  return (url) => new LagSimulatingNetworkConnection(innerFactory(url), config);
}

/**
 * Wraps an `INetworkServer` so every accepted connection is wrapped with
 * {@link LagSimulatingNetworkConnection}.
 */
export class LagSimulatingNetworkServer implements INetworkServer {
  public readonly onConnection: Delegate<[INetworkConnection], void> = new Delegate();
  public readonly onError: Delegate<[Error], void> = new Delegate();

  constructor(
    private readonly inner: INetworkServer,
    private readonly config: LagSimulationConfig
  ) {
    this.inner.onConnection.add((connection) => {
      this.onConnection.invoke(new LagSimulatingNetworkConnection(connection, this.config));
    });
    this.inner.onError.add((error) => {
      this.onError.invoke(error);
    });
  }

  public isListening(): boolean {
    return this.inner.isListening();
  }

  public listen(port: number): Promise<void> {
    return this.inner.listen(port);
  }

  public close(): Promise<void> {
    return this.inner.close();
  }
}

/**
 * Returns a {@link NetworkServerFactory} that wraps the inner transport with
 * lag simulation. Share the same `config` object to update all connections.
 */
export function createLagSimulatingServerFactory(
  innerFactory: NetworkServerFactory,
  config: LagSimulationConfig
): NetworkServerFactory {
  return () => new LagSimulatingNetworkServer(innerFactory(), config);
}
